/**
 * «Накладные» (до 21.10.2026 — «Приёмка», D267) — очередь слева, накладная справа (§5.1 п. 4).
 *
 * Адреса: `/receipts?section=check` — очередь раздела, `/receipts/123` —
 * накладная (раздел очереди едет рядом в `?section=`, чтобы очередь слева
 * не прыгала). Дубли — раздел `dup`: их список стоит в самой очереди,
 * потому что открывать у дубля нечего — только «Удалил» или «Разные».
 *
 * На телефоне — одна колонка: очередь, а по нажатию — накладная с «назад».
 */

import { useState } from 'react';
import { Denied } from '@shell/states';
import type { Navigation, SectionCode } from '../api';
import { canSee } from '../nav';
import { navigate, useRoute } from '../router';
import { Corrections } from './receipts/Corrections';
import { Detail } from './receipts/Detail';
import { Queue, SECTION_CODES } from './receipts/Queue';
import { Icon } from '../icons';

function sectionOf(params: URLSearchParams): SectionCode {
  const value = params.get('section');
  return (SECTION_CODES as string[]).includes(value ?? '') ? (value as SectionCode) : 'check';
}

export function queuePath(section: SectionCode): string {
  return `/receipts?section=${section}`;
}

export function receiptPath(id: number, section: SectionCode): string {
  return `/receipts/${id}?section=${section}`;
}

const QUEUE_HIDDEN_KEY = 'portal.receipts.queue.hidden';

/** Спрятана ли очередь слева: выбор запоминается в браузере. Хранилище может
 *  быть недоступно — тогда просто не запоминаем. */
function useQueueHidden(): [boolean, (value: boolean) => void] {
  const [hidden, setState] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(QUEUE_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const set = (value: boolean) => {
    setState(value);
    try {
      window.localStorage.setItem(QUEUE_HIDDEN_KEY, value ? '1' : '0');
    } catch {
      /* без хранилища */
    }
  };
  return [hidden, set];
}

export function Receipts({ nav }: { nav: Navigation }) {
  const route = useRoute();
  const [queueHidden, setQueueHidden] = useQueueHidden();
  if (!canSee('receipts', nav)) return <Denied />;

  // Исправления в StoreHouse (O15, D268) — своя страница во всю ширину:
  // таблица «было → станет» и фото рядом в колонку справа не помещаются.
  if (route.path === '/receipts/fixes') {
    return (
      <div className="receipts receipts--fixes">
        <button type="button" className="back-link" onClick={() => navigate(queuePath('check'))}>
          <Icon name="back" size={16} /> К накладным
        </button>
        <Corrections />
      </div>
    );
  }

  const section = sectionOf(route.params);
  const match = /^\/receipts\/(\d+)$/.exec(route.path);
  const id = match ? Number(match[1]) : null;
  // Прятать есть смысл, только когда справа открыта накладная: без неё
  // пустая правая часть и спрятанный список — это пустой экран.
  const hidden = queueHidden && id !== null;

  return (
    <div
      className={`receipts${id !== null ? ' receipts--open' : ''}${hidden ? ' receipts--queue-hidden' : ''}`}
    >
      {hidden && (
        <button
          type="button"
          className="queue-rail"
          onClick={() => setQueueHidden(false)}
          aria-label="Показать список накладных"
          title="Показать список накладных"
        >
          <Icon name="panel" size={18} />
          <span className="queue-rail__label">Накладные</span>
        </button>
      )}
      <Queue
        section={section}
        selectedId={id}
        onSection={(next) => navigate(queuePath(next))}
        onOpen={(receiptId) => navigate(receiptPath(receiptId, section))}
        onCollapse={id !== null ? () => setQueueHidden(true) : undefined}
        money={Boolean(nav.services.ops?.rights?.payments)}
      />
      <div className="receipts__work">
        {id !== null ? (
          <>
            <BackToQueue section={section} />
            <Detail id={id} />
          </>
        ) : (
          <div className="receipts__placeholder">
            <Icon name="receipts" size={28} />
            <p>
              {section === 'dup'
                ? 'Дубли — один документ заведён в StoreHouse несколько раз. Удалите лишние в StoreHouse и отметьте «Удалил»; если документы разные — «Разные».'
                : 'Выберите накладную слева — фото откроется рядом с черновиком StoreHouse.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BackToQueue({ section }: { section: SectionCode }) {
  return (
    <button type="button" className="back-link" onClick={() => navigate(queuePath(section))}>
      <Icon name="back" size={16} /> К очереди
    </button>
  );
}
